import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import $ from 'jquery';
import { SubSink } from 'subsink';
import { AppService } from '../app.service';
import { AuthLegacyService } from '../auth/auth-legacy.service';
import { IConfigService } from '../config/iconfigservice';
import { FeatureToggle, InterviewCloseEnum, RolesEnum } from '../enums';
import { InterviewCloseComponent } from '../interview/end-of-interview/interview-close.component';
import { CaseNotificationsService, Environment, EnvironmentService, FeatureToggleService, IdleService, ObservableService, RoutesEnum, RoutingService, ThemeService, UtilityService } from '../services';
import { FeatureManagerService } from '../services/global/feature-manager/feature-manager.service';
import { Regex } from '@Utils';
import { Observable } from 'rxjs';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { UserAvailabilityService } from '../services/user-availability.service';
import { SharedModule } from '@Shared/shared.module';
import { NotificationsComponent } from '@Components/notifications/notifications.component';
import { CaseHistoryDropdownComponent } from '@Components/case-history-dropdown/case-history-dropdown.component';

type MenuItem = {
    title: string;
    icon: string;
    route: RoutesEnum;
    ACL: string;
    aclPermissionType?: string; // TECH DEBT: This does not appear to be used. `checkAcl()` doesn't use it.
    disabled: boolean;
    hasAccess: boolean;
    children: MenuItem[]; // TECH DEBT: This does not appear to be used. No menu item uses children anymore.
}

@Component({
    selector: 'app-navigation',
    templateUrl: './navigation.component.html',
    styleUrls: ['./navigation.component.scss'],
    imports: [SharedModule, RouterOutlet, NotificationsComponent, CaseHistoryDropdownComponent]
})
export class NavigationComponent implements OnInit, OnDestroy {
    @Input() showSideNav = false;
    navOption = 'dashboard';
    closeDialogRef: MatDialogRef<InterviewCloseComponent>;
    userId: string;
    username: string;
    userRole: string;
    isApplicant = false;
    environment: Environment;
    acl: any = [];
    themeList = [];
    activeMenu: any = '';
    activeSubMenu: any = '';
    fullAccessNavItems = ['System Data'];
    displayEnvironmentName = false;
    startText = 'Start Interview';
    isMyAccountEnabled = false;
    unavailabilityScheduleIsEnabled: boolean = false;

    availability$!: Observable<boolean>;

    menu: MenuItem[] = [        
        { title: 'Dashboard', icon: 'swap_driving_apps_wheel', route: RoutesEnum.dashboard, ACL: 'Dashboard', aclPermissionType: 'V', disabled: false, hasAccess: false, children: [] },
        { title: 'My Dashboards', icon: 'dashboard', route: RoutesEnum.myDashboard, ACL: 'My Dashboard', aclPermissionType: 'V', disabled: false, hasAccess: false, children: [] },
        { title: 'Clients', icon: 'contacts_product', route: RoutesEnum.clients, ACL: 'ManageClients', aclPermissionType: 'V', disabled: false, hasAccess: false, children: [] },
        { title: 'My Cases', icon: 'format_list_bulleted', route: RoutesEnum.myCases, ACL: 'MyCases', aclPermissionType: 'V', disabled: false, hasAccess: false, children: [] },
        { title: 'Case Manager', icon: 'checklist', route: RoutesEnum.caseMananger, ACL: 'CaseManager', aclPermissionType: 'V', disabled: false, hasAccess: false, children: [] },
        { title: 'Audit', icon: 'shield', route: RoutesEnum.audit, ACL: 'Audit', aclPermissionType: 'V', disabled: false, hasAccess: false, children: [] },
        { title: 'Roles', icon: 'id_card', route: RoutesEnum.roles, ACL: 'ManageRoles', aclPermissionType: 'V', disabled: false, hasAccess: false, children: [] },
        { title: 'Users', icon: 'account_circle', route: RoutesEnum.userManagement, ACL: 'Users', aclPermissionType: 'V', disabled: false, hasAccess: false, children: [] },
        { title: 'Teams', icon: 'groups', route: RoutesEnum.teams, ACL: 'View Teams', aclPermissionType: 'V', disabled: false, hasAccess: false, children: [] },
        { title: this.startText, icon: 'description', route: RoutesEnum.accounts, ACL: 'Interview', disabled: false, hasAccess: false, children: [] },
        { title: 'Reports', icon: 'chart_data', route: RoutesEnum.reports, ACL: 'Reports', disabled: false, hasAccess: false, children: [] },
        { title: 'Call Manager', icon: 'phone_enabled', route: RoutesEnum.callManager, ACL: 'CallManager', disabled: false, hasAccess: false, children: [] },
        { title: 'System', icon: 'desktop_cloud', route: RoutesEnum.system, ACL: 'System Data', disabled: false, hasAccess: false, children: [] }
    ];

    private subs = new SubSink();

    isPurlV2Active = false;
    hasMyAccountViewPermission: boolean = false;
    hasAvailabilityToggleViewPermission: boolean = false;
    userIsTeamAdmin: boolean = false;
    isCaseHistoryEnabled: boolean = false;

    constructor(
        private router: Router,
        private appService: AppService,
        public authService: AuthLegacyService,
        public configService: IConfigService,
        private routingService: RoutingService,
        private idleService: IdleService,
        private environmentService: EnvironmentService,
        private caseNotificationsService: CaseNotificationsService,
        private featureToggleService: FeatureToggleService,
        public dialog: MatDialog,
        private featureManagerService: FeatureManagerService,
        private utilityService: UtilityService,
        private themeService: ThemeService,
        private observableService: ObservableService,
        public availabilityService: UserAvailabilityService
    ) {
    }

    //#region Helpers

    updateMenuAccess(menuItem: MenuItem) {
        if (menuItem.ACL === '') {
            menuItem.hasAccess = true;
            return;
        }

        const access = this.fullAccessNavItems.some(x => x === menuItem.ACL) ? 'F' : 'R';
        menuItem.hasAccess = this.appService.checkACL(menuItem.ACL, access);

        if (menuItem.route === RoutesEnum.myCases) {
            menuItem.hasAccess = !this.appService.userHasMyCasesQueue;
        }
    }

    updateSideNav(route: string, url: string): void {
        if (url.includes('configuration')) {
            this.showSideNav = false;
            return;
        }

        if (route === 'dashboard' || route === 'metrics') {
            this.showSideNav = false;
        } else {
            this.showSideNav = true;
        }

        this.menu.forEach(menuItem => {
            if (menuItem.route === route) {
                this.activeMenu = menuItem.title;
                this.activeSubMenu = '';
            } else {
                menuItem.children.forEach(subMenuItem => {
                    if (subMenuItem.route === route) {
                        this.activeMenu = menuItem.title;
                        this.activeSubMenu = subMenuItem.title;
                    }
                });
            }
        });
    }

    handleLogout(): void {
        this.utilityService.handleLogout();
    }

    //#endregion
    //#region Subscriptions

    subscribeFeatureFlags() {
        this.featureManagerService.isEnabled(FeatureToggle.GlobalTextInterviewToApplication).subscribe(result => {
            if (result) {
                this.startText = 'Start Application';

                const menuItem = this.menu.find(x => x.ACL === 'Interview');
                menuItem.title = this.startText;
            }
        });

        this.featureManagerService.isEnabled(FeatureToggle.GlobalEnableMyAccount).subscribe(isEnabled => {
            this.isMyAccountEnabled = isEnabled;
        });

        this.featureManagerService.isEnabled(FeatureToggle.GlobalEnableMyDashboard).subscribe(isEnabled => {
            const myDashboardMenuItem = this.menu.find(x => x.route == RoutesEnum.myDashboard);
            const hasAccess = myDashboardMenuItem.hasAccess && isEnabled && this.appService.userHasMyCasesQueue;
            myDashboardMenuItem.hasAccess = hasAccess;
        });

        this.featureManagerService.isEnabled(FeatureToggle.GlobalEnableUnavailabilitySchedule).subscribe((result) => {
            if (result) {
                this.unavailabilityScheduleIsEnabled = true;
            }
        });

        this.featureManagerService.isEnabled(FeatureToggle.DevelopmentFeatureAuditUser, this.userId).subscribe(isEnabled => {
            const auditMenuItem = this.menu.find(x => x.route == RoutesEnum.audit);
            if (auditMenuItem) {
                // If the feature is disabled, we want it to override any other access values.
                if (!isEnabled) auditMenuItem.hasAccess = isEnabled;
                // Merge with existing access (role permissions could be disabled so keep it that way)
                else auditMenuItem.hasAccess = auditMenuItem.hasAccess && isEnabled;
            }
        });

        this.featureManagerService.isEnabled(FeatureToggle.ClientEnableCaseHistory).subscribe(isEnabled => {
            this.isCaseHistoryEnabled = this.appService.hasViewPermission('My Case History') && this.checkUserHasSingleClient() && isEnabled;            
        });

        this.displayEnvironmentName = this.featureManagerService.getByName(FeatureToggle.GlobalDisplayEnvironment).enabled;
    }

    //#endregion
    //#region Lifecycle

    ngOnInit() {
        this.menu.forEach(menuItem => this.updateMenuAccess(menuItem));

        this.subscribeFeatureFlags();
        this.hasMyAccountViewPermission = this.appService.hasViewPermission('My Account');
        this.hasAvailabilityToggleViewPermission = this.appService.hasViewPermission('Availability Toggle');
        this.userIsTeamAdmin = this.appService.userIsTeamAdmin;

        this.userId = this.appService.getUserId();
        this.username = this.appService.getUsername();
        this.userRole = this.appService.getUserRoleName();

        this.isApplicant = this.userRole == RolesEnum.Applicant;
        this.subs.add(this.environmentService.getEnvironment().subscribe(env => this.environment = env));
        this.subs.add(this.router.events.subscribe((val) => {
            if (val instanceof NavigationEnd) {
                const route = val.url.split('/')[2];
                this.updateSideNav(route, val.url);
            }
        }));


        this.idleService.startIdleTimer();

        this.isPurlV2Active = this.themeService.isV2ThemeCurrent();

        if (this.hasAvailabilityToggleViewPermission) {
            this.getUserAvailability();
        }
    }

    ngOnDestroy() {
        this.idleService.stopIdleTimer();
        this.subs.unsubscribe();
    }

    //#endregion
    //#region Handlers

    signOut() {
        const inInterview = this.router.url.match(Regex.InterviewRoute);
        const onCaseSummary = this.router.url.match(Regex.CaseSummaryRoute);
        if (inInterview) {
            this.closeDialogRef = this.dialog.open(InterviewCloseComponent, {
                width: '300px',
                data: { clickSource: InterviewCloseEnum.SignOut },
                disableClose: false
            });

            this.closeDialogRef.afterClosed().subscribe(data => {
                if (data === InterviewCloseEnum.SignOut) {
                    this.caseNotificationsService.removeUserFromCase();
                    this.handleLogout();
                }
            });
        } else if (onCaseSummary) {
            this.caseNotificationsService.removeUserFromCase();
            this.handleLogout();
        } else {
            this.handleLogout();
        }
    }

    selectMenu(event, item) {
        event.stopPropagation();
        if (!item.disabled) {
            if (item.route) {
                this.routingService.navigateToRoute(item.route);
            }
        }
    }

    toggleClick() {
        $('#sidenav').toggleClass('hidenav');

        setTimeout(() => {
            this.appService.getSideMenuInfo($('#sidenav').hasClass('hidenav'));
        }, 10);
    }

    resetTimeCounter() {
        this.idleService.resetIdleTimer();
    }

    exitApplication(){
        this.observableService.exitApplication.next(true);
    }

    //#endregion
    //#region TECH DEBT: Unused Methods (safe to delete?)

    // These methods don't appear to be used in the template(view) or this component. Verify they're not used anywhere and remove.

    gotoDashboard() {
        this.routingService.navigateToRoute(RoutesEnum.dashboard);
    }

    goToMyAccount() {
        if (this.isMyAccountEnabled && this.appService.hasViewPermission('My Account')) {
            this.routingService.navigateToRoute(RoutesEnum.myAccount);
        }
    }

    getUserAvailability() {
        this.availability$ = this.availabilityService.getAvailability(this.userId);
    }

    toggleUserAvailability(event: MatSlideToggleChange) {
        const isAvailable = event.checked;
        event.source.checked = !isAvailable;
        this.availabilityService.updateAvailability(this.userId, event.checked, true).subscribe((response) => {
        });
    }

    toggleMenu(itemTitle, event) {
        if (this.activeMenu === itemTitle) {
            this.activeMenu = '';
            event.stopPropagation();
        }
    }

    checkUserHasSingleClient(): boolean {
        const userClients = this.appService.getUserDataItems('userClients');
        return Array.isArray(userClients) && new Set(userClients.map(({ clientId }) => clientId)).size === 1;
    }

    //#endregion
}
